/**
 * Marvel IPTV Uploader Page
 * 
 * Premium themed upload form for Marvel Stadium IPTV content.
 * Layout: Dark header → Teal accent line → Light content area
 * 
 * Supports:
 * - General IPTV Content
 * - Stadium Square
 * - Function Rooms (Landscape & Portrait)
 * - LED Walls (Quill, Rogers)
 * - Gates (4, 6, 8, 9, Half)
 * - Aisle 31
 */

import React, { useState, useCallback, useRef } from "react";
import { useVFContext } from "../../../context/VFContext";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { useToast } from "../../../components/ui/UploaderToast";
import UploaderSection from "../../../components/uploaders/UploaderSection";
import UserDetailsFields from "../../../components/uploaders/UserDetailsFields";
import UploadSuccessModal from "../../../components/modal/UploadSuccessModal";
import {
  IPTV_PLACEMENTS,
  IPTV_PLACEMENT_GROUPS,
  GROUPED_FOLDER_PLACEMENTS,
  ASSET_NAME_PLACEMENTS,
  UPLOADER_SPECS,
  METADATA_WEBHOOK,
} from "../../../config/marvelIptvSpecs";
import { generateSessionId, generateCrockfordTimeId } from "../../../services/idGenerator";

// ============================================================
// STYLES - Premium Theme
// ============================================================
const styles = {
  // Page
  page: {
    minHeight: "100vh",
    height: "100%",
    backgroundColor: "#f7f7f5",
    fontFamily: "'Space Grotesk', -apple-system, sans-serif",
  },
  
  // Dark header
  header: {
    backgroundColor: "#111114",
    padding: "18px 32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: "#fff",
    margin: 0,
  },
  headerSub: {
    fontSize: 13,
    color: "rgba(255,255,255,0.5)",
    marginTop: 2,
  },
  venueBadge: {
    backgroundColor: "rgba(47, 214, 181, 0.15)",
    border: "1px solid rgba(47, 214, 181, 0.4)",
    color: "#2fd6b5",
    padding: "6px 14px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
  },
  
  // Teal accent line under header
  accentLine: {
    height: 3,
    background: "linear-gradient(90deg, #2fd6b5, #84cc16)",
  },
  
  // Content
  content: {
    padding: "32px 40px 48px",
    maxWidth: 1100,
    margin: "0 auto",
  },
  
  // Section labels
  sectionLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: 12,
  },
  
  // Placement type selector
  typeSection: {
    marginBottom: 28,
  },
  typeRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  typeBtn: {
    padding: "10px 18px",
    fontSize: 13,
    fontWeight: 500,
    color: "#555",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    cursor: "pointer",
    transition: "all 0.15s ease",
    fontFamily: "inherit",
  },
  typeBtnHover: {
    borderColor: "#bbb",
    backgroundColor: "#fafafa",
    color: "#333",
  },
  typeBtnActive: {
    backgroundColor: "#111114",
    borderColor: "#111114",
    color: "#fff",
    fontWeight: 600,
  },
  
  // Upload section with teal highlight
  uploadSection: {
    backgroundColor: "#fff",
    borderRadius: 14,
    border: "2px solid #2fd6b5",
    boxShadow: "0 0 0 4px rgba(47, 214, 181, 0.1), 0 4px 20px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
    marginBottom: 28,
  },
  uploadHeader: {
    backgroundColor: "#111114",
    padding: "14px 24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  uploadTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  uploadTitleIcon: {
    color: "#2fd6b5",
  },
  uploadBody: {
    padding: 24,
  },
  
  // Notes section
  notesSection: {
    marginBottom: 32,
  },
  textarea: {
    width: "100%",
    padding: "14px 16px",
    fontSize: 14,
    color: "#333",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: 10,
    resize: "vertical",
    minHeight: 80,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  },
  textareaFocus: {
    borderColor: "#2fd6b5",
    boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.1)",
  },
  charCount: {
    fontSize: 11,
    color: "#999",
    textAlign: "right",
    marginTop: 6,
  },
  
  // Footer
  footer: {
    paddingTop: 24,
    borderTop: "1px solid #e5e5e3",
    display: "flex",
    justifyContent: "flex-end",
  },
  submitBtn: {
    padding: "12px 32px",
    fontSize: 15,
    fontWeight: 600,
    color: "#000",
    background: "linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "flex",
    alignItems: "center",
    gap: 8,
    transition: "all 0.15s ease",
  },
  submitBtnHover: {
    transform: "translateY(-1px)",
    boxShadow: "0 6px 16px rgba(47, 214, 181, 0.35)",
  },
  submitBtnDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
    transform: "none",
    boxShadow: "none",
  },
  
  // UserDetailsFields compatibility
  grid2: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 16,
    marginBottom: 28,
  },
  field: {},
  label: {
    fontSize: 11,
    fontWeight: 700,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: 8,
    display: "block",
  },
  input: {
    width: "100%",
    padding: "11px 14px",
    fontSize: 14,
    color: "#333",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  },
  inputFocus: {
    borderColor: "#2fd6b5",
    boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.1)",
  },
  
  // Placeholder state
  placeholderBox: {
    backgroundColor: "#fafaf8",
    borderRadius: 10,
    border: "2px dashed #ddd",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 32,
    color: "#999",
    fontSize: 14,
    textAlign: "center",
    height: "100%",
    minHeight: 200,
  },

  // Asset Name field (for Suite placement)
  assetNameRow: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 4,
  },
  assetNameFieldWrap: {
    width: 340,
  },
  assetNameLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: "#666",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    marginBottom: 6,
    display: "block",
  },
  assetNameInput: {
    width: "100%",
    padding: "10px 14px",
    fontSize: 14,
    color: "#333",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  },
  assetNameInputFocus: {
    borderColor: "#2fd6b5",
    boxShadow: "0 0 0 3px rgba(47, 214, 181, 0.1)",
  },
  assetNameInputLocked: {
    backgroundColor: "#f3f4f6",
    color: "#6b7280",
    borderColor: "#e5e7eb",
    cursor: "default",
  },
  assetNameBtn: {
    padding: "10px 18px",
    fontSize: 13,
    fontWeight: 600,
    color: "#000",
    background: "linear-gradient(135deg, #2fd6b5 0%, #84cc16 100%)",
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    transition: "all 0.15s ease",
  },
  assetNameBtnDisabled: {
    opacity: 0.4,
    cursor: "not-allowed",
  },
  assetNameEditBtn: {
    padding: "10px 18px",
    fontSize: 13,
    fontWeight: 600,
    color: "#555",
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "inherit",
    whiteSpace: "nowrap",
    transition: "all 0.15s ease",
  },
  assetNameEditBtnWarning: { color: "#92400e", backgroundColor: "#fffbeb", borderColor: "#f59e0b" },
  assetNamePreview: {
    fontSize: 12,
    color: "#888",
    marginTop: 6,
    fontFamily: "'SF Mono', 'Fira Code', monospace",
  },
  uploadersDisabledOverlay: {
    opacity: 0.4,
    pointerEvents: "none",
    filter: "grayscale(0.3)",
  },
};

// ============================================================
// TYPE BUTTON
// ============================================================
function TypeButton({ label, isActive, onClick, disabled }) {
  const [hovered, setHovered] = useState(false);
  
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...styles.typeBtn,
        ...(hovered && !isActive ? styles.typeBtnHover : {}),
        ...(isActive ? styles.typeBtnActive : {}),
        ...(disabled ? { opacity: 0.5, cursor: "not-allowed" } : {}),
      }}
    >
      {label}
    </button>
  );
}

// ============================================================
// CONSTANTS
// ============================================================
const MAX_DESC = 250;

// ============================================================
// MAIN COMPONENT
// ============================================================
export function MarvelIPTVPage() {
  const context = useVFContext();
  const { email: authEmail, name: authName } = useAuthUser();
  const { showError, ToastComponent } = useToast();

  const [sessionId, setSessionId] = useState(() => generateSessionId());
  const [submissionTimeId, setSubmissionTimeId] = useState(() => generateCrockfordTimeId());
  const [name, setName] = useState("");
  const [email, setEmail] = useState(authEmail || "");
  const [description, setDescription] = useState("");
  const [activePlacement, setActivePlacement] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [isSubmitHovered, setIsSubmitHovered] = useState(false);

  // Asset Name state (for Suite placement, like LED Tiled/360)
  const [assetName, setAssetName] = useState("");
  const [isAssetNameLocked, setIsAssetNameLocked] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [uploadResults, setUploadResults] = useState(() => {
    const results = {};
    Object.keys(UPLOADER_SPECS).forEach((key) => {
      const spec = UPLOADER_SPECS[key];
      results[key] = {
        contentGroup: spec.contentGroup,
        ledType: spec.ledType,
        assets: [],
      };
    });
    return results;
  });

  const activeUploads = useRef(0);

  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastUploadInfo, setLastUploadInfo] = useState(null);

  const needsAssetName = activePlacement && ASSET_NAME_PLACEMENTS.has(activePlacement);
  const isSuitePlacement = activePlacement === "IPTV-SUITE";

  const activeUploaderIds = activePlacement
    ? IPTV_PLACEMENT_GROUPS[activePlacement] || []
    : [];

  const handlePlacementChange = useCallback((newPlacement) => {
    if (activePlacement !== newPlacement) {
      // Reset upload results when switching placements
      setUploadResults(() => {
        const results = {};
        Object.keys(UPLOADER_SPECS).forEach((key) => {
          const spec = UPLOADER_SPECS[key];
          results[key] = { contentGroup: spec.contentGroup, ledType: spec.ledType, assets: [] };
        });
        return results;
      });
    }
    setActivePlacement(newPlacement);
    // Reset asset name when switching placements
    setAssetName("");
    setIsAssetNameLocked(false);
  }, [activePlacement]);

  const handleUploadComplete = useCallback(({ uploadId, file }) => {
    activeUploads.current = Math.max(0, activeUploads.current - 1);
    if (activeUploads.current === 0) setIsUploading(false);
    setUploadResults((prev) => ({
      ...prev,
      [uploadId]: {
        ...prev[uploadId],
        assets: [...(prev[uploadId]?.assets || []), file],
      },
    }));
  }, []);

  const handleUploadStart = useCallback(() => {
    activeUploads.current += 1;
    setIsUploading(true);
  }, []);

  const handleUploadCancel = useCallback(({ uploadId, fileId }) => {
    activeUploads.current = Math.max(0, activeUploads.current - 1);
    if (activeUploads.current === 0) setIsUploading(false);
    setUploadResults((prev) => ({
      ...prev,
      [uploadId]: {
        ...prev[uploadId],
        assets: prev[uploadId]?.assets?.filter((a) => a.id !== fileId) || [],
      },
    }));
  }, []);

  const validatePlacementComplete = useCallback(() => {
    if (!activePlacement) return false;
    const group = IPTV_PLACEMENT_GROUPS[activePlacement];
    if (!Array.isArray(group) || !group.length) return false;
    return group.every((uploadId) => {
      const spec = UPLOADER_SPECS[uploadId];
      if (!spec) return false;
      const uploaded = uploadResults[uploadId]?.assets?.length || 0;
      // Suite requires exactly requiredFiles per bucket
      if (isSuitePlacement) return uploaded === (spec.requiredFiles ?? 1);
      return uploaded > 0;
    });
  }, [activePlacement, uploadResults, isSuitePlacement]);

  const buildContentUpload = useCallback(() => {
    const ContentUpload = {};
    const group = IPTV_PLACEMENT_GROUPS[activePlacement] || [];
    group.forEach((uploadId) => {
      const bucket = uploadResults[uploadId];
      if (!bucket || !bucket.assets?.length) return;
      ContentUpload[uploadId] = {
        contentGroup: bucket.contentGroup,
        ledType: bucket.ledType,
        assets: bucket.assets,
      };
    });
    return ContentUpload;
  }, [activePlacement, uploadResults]);

  const resetForm = useCallback(() => {
    activeUploads.current = 0;
    setIsUploading(false);
    setUploadResults(() => {
      const results = {};
      Object.keys(UPLOADER_SPECS).forEach((key) => {
        const spec = UPLOADER_SPECS[key];
        results[key] = {
          contentGroup: spec.contentGroup,
          ledType: spec.ledType,
          assets: [],
        };
      });
      return results;
    });
    setSessionId(generateSessionId());
    setSubmissionTimeId(generateCrockfordTimeId());
    setDescription("");
    setActivePlacement(null);
    setAssetName("");
    setIsAssetNameLocked(false);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!activePlacement) {
      showError("Please select an IPTV placement.", { duration: 9000 });
      return;
    }
    if (needsAssetName && !isAssetNameLocked) {
      showError("Please set an Asset Name before submitting.", { duration: 9000 });
      return;
    }
    if (activeUploads.current > 0) {
      showError("Uploads are still in progress.\nPlease wait for all files to finish.", { duration: 12000 });
      return;
    }
    if (!validatePlacementComplete()) {
      showError(
        isSuitePlacement
          ? "All five Suite buckets must have a file uploaded."
          : "Please upload at least one file for the selected IPTV placement.",
        { duration: 12000 }
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        sessionId,
        submissionParent: context.submissionParent,
        completedAt: new Date().toISOString(),
        placementId: activePlacement,
        name: authName || name || context.submitterName,
        email: authEmail || email,
        description,
        venue: "Marvel",
        ...(needsAssetName && assetName ? { assetName } : {}),
        ContentUpload: buildContentUpload(),
      };

      const res = await fetch(METADATA_WEBHOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error(`Submission failed (${res.status})`);
      
      const placementLabel = IPTV_PLACEMENTS.find(p => p.placementId === activePlacement)?.label || activePlacement;
      const isGroupedPlacement = GROUPED_FOLDER_PLACEMENTS.has(activePlacement);

      let assets;
      if (isGroupedPlacement) {
        // Grouped placements (Suite) - show one ID for the group
        const clubCode = context.submitterShortName || "VF";
        const shortContentId = `${clubCode}_${submissionTimeId}`;
        assets = [{ contentId: shortContentId, fileName: `${placementLabel} (${uploadCount} files)` }];
      } else {
        // Single asset placements - show individual files
        const allAssets = [];
        activeUploaderIds.forEach(uploadId => {
          const bucket = uploadResults[uploadId];
          if (bucket?.assets?.length) {
            bucket.assets.forEach(asset => {
              const prefixedName = asset?.prefixedFileName || "";
              const parts = prefixedName.split("_");
              const contentId = parts.length >= 2 ? `${parts[0]}_${parts[1]}` : `${context.submitterShortName || "VF"}_${submissionTimeId}`;
              allAssets.push({
                contentId,
                fileName: asset?.originalFileName || "Uploaded file",
              });
            });
          }
        });
        assets = allAssets;
      }

      setLastUploadInfo({
        assets,
        destination: isSuitePlacement ? "Marvel-IPTV" : `Marvel IPTV - ${placementLabel}`,
      });
      setShowSuccessModal(true);
      
      resetForm();
    } catch (err) {
      console.error("Submission error:", err);
      showError("Something went wrong. Please try again.", { duration: 6000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  const uploadCount = activeUploaderIds.reduce((sum, id) => sum + (uploadResults[id]?.assets?.length || 0), 0);

  return (
    <div style={styles.page}>
      {/* Dark Header */}
      <header style={styles.header}>
        <div>
          <h1 style={styles.headerTitle}>IPTV Content</h1>
          <p style={styles.headerSub}>Upload content for Marvel Stadium IPTV displays</p>
        </div>
        <span style={styles.venueBadge}>Marvel Stadium</span>
      </header>
      
      {/* Teal accent line */}
      <div style={styles.accentLine} />

      {/* Content */}
      <main style={styles.content}>
        <form onSubmit={handleSubmit}>
          
          {/* User Details */}
          <UserDetailsFields
            name={name}
            setName={setName}
            email={email}
            setEmail={setEmail}
            focusedField={focusedField}
            setFocusedField={setFocusedField}
            styles={styles}
          />

          {/* IPTV Placement Selector */}
          <div style={styles.typeSection}>
            <div style={styles.sectionLabel}>IPTV Placement</div>
            <div style={styles.typeRow}>
              {IPTV_PLACEMENTS.map((p) => (
                <TypeButton
                  key={p.placementId}
                  label={p.label}
                  isActive={activePlacement === p.placementId}
                  onClick={() => handlePlacementChange(p.placementId)}
                  disabled={isSubmitting}
                />
              ))}
            </div>
          </div>

          {/* Upload Section */}
          <div style={styles.uploadSection}>
            <div style={styles.uploadHeader}>
              <h2 style={styles.uploadTitle}>
                <svg style={styles.uploadTitleIcon} width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 3v9M5 7l4-4 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 13v2a1 1 0 001 1h10a1 1 0 001-1v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Upload Files
              </h2>
              {uploadCount > 0 && (
                <span style={{
                  backgroundColor: "#2fd6b5",
                  color: "#000",
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 10,
                }}>
                  {uploadCount} file{uploadCount !== 1 ? "s" : ""} ready
                </span>
              )}
            </div>
            
            <div style={styles.uploadBody}>
              {activePlacement && activeUploaderIds.length > 0 ? (
                <>
                  {/* Asset Name field for Suite placement */}
                  {needsAssetName && (
                    <div style={{ marginBottom: 8 }}>
                      <label style={styles.assetNameLabel}>Asset Name <span style={{ color: "#dc2626" }}>*</span></label>
                      <div style={styles.assetNameRow}>
                        <div style={styles.assetNameFieldWrap}>
                          <input
                            type="text"
                            value={assetName}
                            onChange={(e) => {
                              const sanitised = e.target.value.replace(/[^a-zA-Z0-9 _-]/g, "");
                              setAssetName(sanitised.slice(0, 30));
                            }}
                            disabled={isAssetNameLocked || isSubmitting}
                            placeholder="Lumen8 Asset"
                            maxLength={30}
                            onFocus={() => setFocusedField("assetName")}
                            onBlur={() => setFocusedField(null)}
                            style={{
                              ...styles.assetNameInput,
                              ...(focusedField === "assetName" && !isAssetNameLocked ? styles.assetNameInputFocus : {}),
                              ...(isAssetNameLocked ? styles.assetNameInputLocked : {}),
                            }}
                          />
                        </div>
                        {!isAssetNameLocked ? (
                          <button
                            type="button"
                            disabled={!assetName.trim() || isSubmitting}
                            onClick={() => {
                              setAssetName((prev) => prev.replace(/\s+/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, ""));
                              setIsAssetNameLocked(true);
                            }}
                            style={{
                              ...styles.assetNameBtn,
                              ...(!assetName.trim() ? styles.assetNameBtnDisabled : {}),
                            }}
                          >
                            Set Name
                          </button>
                        ) : (
                          <button
                            type="button"
                            disabled={isSubmitting || isUploading}
                            onClick={() => {
                              if (uploadCount > 0) {
                                if (!window.confirm("Changing the Asset Name after uploading files will reset the form and start a new session. All uploaded files will be lost.\n\nDo you want to continue?")) return;
                                resetForm();
                                return;
                              }
                              setAssetName("");
                              setIsAssetNameLocked(false);
                            }}
                            title={isUploading ? "Upload in progress \u2014 please wait" : uploadCount > 0 ? "Warning: this will reset the form and start a new session" : ""}
                            style={{
                              ...styles.assetNameEditBtn,
                              ...(isUploading ? { opacity: 0.4, cursor: "not-allowed" } : uploadCount > 0 ? styles.assetNameEditBtnWarning : {}),
                            }}
                          >
                            {uploadCount > 0 && !isUploading ? "Edit \u26a0" : "Edit"}
                          </button>
                        )}
                      </div>
                      {!isAssetNameLocked && (
                        <div style={{ fontSize: 11, color: "#999", textAlign: "right", marginTop: 4, width: 340 }}>{assetName.length}/30</div>
                      )}
                      {isAssetNameLocked && (
                        <div style={styles.assetNamePreview}>
                          Folder: {context.submitterShortName || "VF"}_{submissionTimeId}_SUITE_{assetName}
                        </div>
                      )}
                      {!isAssetNameLocked && (
                        <div style={{ fontSize: 12, color: "#999", marginTop: 4 }}>
                          Set the asset name to enable uploaders. Letters, numbers, hyphens and underscores only — spaces will be converted to underscores.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Uploaders — disabled until asset name is set (for Suite) */}
                  <div style={needsAssetName && !isAssetNameLocked ? styles.uploadersDisabledOverlay : {}}>
                    {activeUploaderIds.map((uploadId) => {
                      const spec = UPLOADER_SPECS[uploadId];
                      if (!spec) return null;
                      // For Suite, override groupFolder to include assetName
                      const modifiedSpec = isSuitePlacement && isAssetNameLocked
                        ? { ...spec, groupFolder: `SUITE_${assetName}` }
                        : spec;
                      return (
                        <UploaderSection
                          key={`${sessionId}-${uploadId}`}
                          uploadId={uploadId}
                          spec={modifiedSpec}
                          context={context}
                          sessionId={sessionId}
                          submissionTimeId={submissionTimeId}
                          onUploadComplete={handleUploadComplete}
                          onUploadStart={handleUploadStart}
                          onUploadCancel={handleUploadCancel}
                        />
                      );
                    })}
                  </div>
                </>
              ) : (
                <div style={styles.placeholderBox}>
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" style={{ marginBottom: 12, color: "#ccc" }}>
                    <path d="M20 8v16M12 16l8-8 8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M6 28v4a2 2 0 002 2h24a2 2 0 002-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  <p>Select an IPTV placement above to start uploading</p>
                </div>
              )}
            </div>
          </div>

          {/* Notes Section */}
          <div style={styles.notesSection}>
            <div style={styles.sectionLabel}>Notes (Optional)</div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESC))}
              onFocus={() => setFocusedField("notes")}
              onBlur={() => setFocusedField(null)}
              placeholder="Add any notes for the operations team..."
              style={{
                ...styles.textarea,
                ...(focusedField === "notes" ? styles.textareaFocus : {}),
              }}
            />
            <div style={styles.charCount}>{description.length}/{MAX_DESC}</div>
          </div>

          {/* Footer */}
          <div style={styles.footer}>
            <button
              type="submit"
              disabled={isSubmitting || !activePlacement || uploadCount === 0 || (needsAssetName && !isAssetNameLocked)}
              onMouseEnter={() => setIsSubmitHovered(true)}
              onMouseLeave={() => setIsSubmitHovered(false)}
              style={{
                ...styles.submitBtn,
                ...(isSubmitHovered && !isSubmitting && activePlacement && uploadCount > 0 && !(needsAssetName && !isAssetNameLocked) ? styles.submitBtnHover : {}),
                ...((isSubmitting || !activePlacement || uploadCount === 0 || (needsAssetName && !isAssetNameLocked)) ? styles.submitBtnDisabled : {}),
              }}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </form>
      </main>

      {ToastComponent}
      
      {/* Upload Success Modal */}
      <UploadSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        assets={lastUploadInfo?.assets || []}
        destination={lastUploadInfo?.destination}
      />
    </div>
  );
}

export default MarvelIPTVPage;
